import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { memo, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type {
	IBodyProps,
	TableBodyCell,
	TableBodyCellElementDefinition,
} from './StrawberryTableBody.types';

function isTableCellElementDefinition(cell: TableBodyCell): cell is TableBodyCellElementDefinition {
	if (!cell) {
		return false;
	}

	return typeof cell === 'object' && Object.hasOwn(cell, 'element');
}

interface IComponentProps {
	textToCopy?: string;
}

const DEFAULT_FONT_SIZE = 'sans/14/400';

const CellContent = ({ textToCopy, children }: PropsWithChildren<IComponentProps>) => (
	<Detail enableHover={!!textToCopy}>
		<Text variant={DEFAULT_FONT_SIZE} disableVariantMapping>
			{children}
		</Text>
		<Icon
			variant="copy"
			className="copy-icon"
			onClick={async () => {
				if (textToCopy) {
					await navigator.clipboard.writeText(textToCopy);
				}
			}}
		/>
	</Detail>
);

const ElementDefinitionCell = ({ content }: { content: TableBodyCellElementDefinition }) => (
	<td>
		<CellContent
			textToCopy={content.textToCopy == null ? undefined : content.textToCopy.toString()}
		>
			{content.element}
		</CellContent>
	</td>
);

const PrimitiveCell = ({ content }: { content: string | number | null }) => (
	<td>
		<CellContent textToCopy={content == null ? undefined : content.toString()}>
			{content}
		</CellContent>
	</td>
);

const ArrayContent = ({ content }: { content: TableBodyCell[] }) => (
	// eslint-disable-next-line react/jsx-no-useless-fragment
	<>
		{content?.map((element, index) => {
			if (isTableCellElementDefinition(element)) {
				return <ElementDefinitionCell key={index} content={element} />;
			}
			return <PrimitiveCell key={index} content={element} />;
		})}
	</>
);

const StrawberryTableBody = ({ content }: IBodyProps): JSX.Element | null => {
	if (Array.isArray(content)) {
		return <ArrayContent content={content} />;
	}

	if (isTableCellElementDefinition(content)) {
		return <ElementDefinitionCell content={content} />;
	}

	return <PrimitiveCell content={content} />;
};

interface Props {
	enableHover: boolean;
}

const Detail = styled.div<Props>`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;

	${({ enableHover }) => {
		if (enableHover) {
			return css`
                :hover {
                    background-color: var(--hover-background-color);
                    border-radius: var(--hover-border-radius);
                    ${({ theme }) => {
						return css`${Icon}.svg {
                color: ${theme.v2.color.secondary[800]};
                fill: ${theme.v2.color.secondary[800]};`;
					}}}

                .copy-icon {
                    display: block;
                }
            }`;
		}
		return;
	}}

	.copy-icon {
		display: none;
		padding: 4px;
	}
`;
const _StrawberryTableBodyExport = memo(StrawberryTableBody);

export { _StrawberryTableBodyExport as StrawberryTableBody };
